import React from 'react'

import { cn } from 'src/shared/utils/cn'

import PulsatingDots from '../animation/pulsating-dots-loader'
import SpeechIcon from '../miscellaneous/speech-icon'

import { Button } from './button'

export interface StartJourneyButtonBaseProps {
  /**
   * Default: green gradient button, with chat icon on the right.
   * Transparent: transparent button, with chat icon on the left.
   * Dark: dark button, with chat icon on the left.
   * Light-gray: light gray button, with chat icon on the right.
   */
  variant?: 'default' | 'transparent' | 'light-gray' | 'dark' | 'no-style'
  className?: string
  onClick?: () => void
  showChatIcon?: boolean
  text?: string
  type?: 'button' | 'submit' | 'reset' | undefined
  /**
   * A custom button component that you can use if you don't want to use the default button.
   * The component should be an element type that takes an onClick prop.
   */
  customButton?: (onClick: () => void) => React.ReactNode
  isSubmitting?: boolean
}

/** Base component for start journey button. Only contains UI. Use variant to change the default styles. */
export default function StartJourneyButtonBase({
  variant = 'default',
  className,
  onClick,
  showChatIcon = true,
  text = 'Start my journey',
  customButton,
  type = 'button',
  isSubmitting = false,
}: StartJourneyButtonBaseProps) {
  if (customButton) {
    return customButton(onClick || (() => {}))
  }

  return (
    <Button
      type={type}
      variant={'default'}
      size="sm"
      className={cn(
        // Default styles
        variant !== 'no-style' &&
          'flex h-[50px] gap-[10px] rounded-full px-5 text-[15px] font-medium text-white xs:h-[60px] xs:text-[17px]',
        // Custom styles
        variant === 'dark' && 'flex-row-reverse bg-primary xs:px-[35px]',
        variant === 'default' && 'flex-row bg-[linear-gradient(94deg,#0A9B56_40.1%,#54F16F_114.01%)] pl-5 pr-[15px]',
        variant === 'transparent' &&
          'flex-row-reverse border-[0.5px] border-white/40 bg-white/15 shadow-[inset_0px_0px_10px_0px_rgba(255,255,255,0.40)] backdrop-blur-[1.5px] xs:px-[30px]',
        variant === 'light-gray' &&
          'w-full max-w-[280px] flex-row justify-between bg-[#F5F5F5] pl-5 pr-[10px] text-muted',
        className,
      )}
      onClick={onClick}
      disabled={isSubmitting}
    >
      {isSubmitting ? (
        <PulsatingDots />
      ) : (
        <>
          {text}
          {showChatIcon && (
            <SpeechIcon
              variant={variant !== 'default' && variant !== 'light-gray' ? 'square' : 'rounded'}
              className={cn(variant === 'default' && 'h-[30px] w-[30px] bg-transparent xs:h-[34px] xs:w-[34px]')}
            />
          )}
        </>
      )}
    </Button>
  )
}
